import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

type FocusPayload = {
  sourceId: string;
  sourceName: string;
  focusedIds: string[];
};

type FocusOnStoreType = {
  sourceId: string | null;
  sourceName: string | null;
  focusedIds: string[];
  setFocus: (payload: FocusPayload) => void;
  clear: () => void;
  isFocused: (id: string) => boolean;
};

const useFocusOnStore = create<FocusOnStoreType>((set, get) => ({
  sourceId: null,
  sourceName: null,
  focusedIds: [],

  setFocus: ({ sourceId, sourceName, focusedIds }) => {
    set({ sourceId, sourceName, focusedIds: Array.from(new Set(focusedIds)) });
  },

  clear: () => {
    if (get().sourceId === null && get().focusedIds.length === 0) return;
    set({ sourceId: null, sourceName: null, focusedIds: [] });
  },

  isFocused: (id: string) => get().focusedIds.includes(id),
}));

export const useIsFocusActive = () =>
  useFocusOnStore((state) => state.sourceId !== null);

export const useFocusSource = () =>
  useFocusOnStore(
    useShallow((state) => ({
      sourceId: state.sourceId,
      sourceName: state.sourceName,
    })),
  );

export const useFocusedIds = () => useFocusOnStore((state) => state.focusedIds);

export const useIsNodeFocused = (id: string) =>
  useFocusOnStore((state) => state.focusedIds.includes(id));

export const { getState: getFocusOnState, setState: setFocusOnState } =
  useFocusOnStore;

export default useFocusOnStore;
